import { Injectable } from '@angular/core';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { ETLMetricRecalculationObj, ETLReportingSetRecalculationObj, SaveRecalculationDataObj } from './cxone-etl-recalculation-modal.model';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class CxoneEtlRecalculationService {
    private reportingSetListChange = new Subject<any>();
    private metricListChange = new Subject<any>();
    private metricDataCache: ETLMetricRecalculationObj[] | null = null;
    private reportingSetDataCache: ETLReportingSetRecalculationObj[] | null = null;
    private isEmitted = undefined;
    private lastMetricListChange: string[];
    private lastMetricReportingSetChange: string[];

    reportingSetListChange$ = this.reportingSetListChange.asObservable();
    metricListChange$ = this.metricListChange.asObservable();

    constructor(private cvApiService: CVApiService) { }

    public async loadRecalculationMetricData(useCacheData: boolean): Promise<ETLMetricRecalculationObj[]> {
        if (this.metricDataCache && useCacheData) {
            // If data is cached, return it as a promise
            return Promise.resolve(this.metricDataCache);
        } else {
            // If not cached, fetch data from the API and cache it
            try {
                const response = await this.cvApiService.get<ETLMetricRecalculationObj[]>('/api/v1/pipeline-config/metric-definitions');
                if (response.status_code >= 200 && response.status_code < 300) {
                    this.metricDataCache = response.data;
                    return response.data;
                } else {
                    throw response;
                }
            } catch (error) {
                console.error('Failed to load metric definitions.');
                throw error;
            }
        }
    }

    public async loadRecalculationReportingSetData(): Promise<ETLReportingSetRecalculationObj[]> {
        if (this.reportingSetDataCache) {
            // If data is cached, return it as a promise
            return Promise.resolve(this.reportingSetDataCache);
        } else {
            // If not cached, fetch data from the API and cache it
            try {
                const response = await this.cvApiService.get<ETLReportingSetRecalculationObj[]>('/api/v1/pipeline-config/reporting-set-definitions');
                if (response.status_code >= 200 && response.status_code < 300) {
                    this.reportingSetDataCache = response.data;
                    return response.data;
                } else {
                    throw response;
                }
            } catch (error) {
                console.error('Failed to load reporting set definitions.');
                throw error;
            }
        }
    }

    public async saveRecalculationData(recalculationData): Promise<SaveRecalculationDataObj> {
        try {
            const response = await this.cvApiService.post<SaveRecalculationDataObj>(`/api/v2/pipeline/recalculate-multiple`, recalculationData);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save recalculation Data.`);
            throw error;
        }
    }

    async getPermissions(requestedPage: string) {
        try {
            const response = await this.cvApiService.get<string[]>(`/api/v1/general/pages/user-permissions/${requestedPage}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            throw error;
        }
    }

    publishReportingSetListChange(data: string[]) {
        this.lastMetricReportingSetChange = data;
        this.reportingSetListChange.next(data);
    }

    publishMetricListChange(data: string[]) {
        this.lastMetricListChange = data;
        this.metricListChange.next(data);
    }

    get getEmittedValue(): string {
        return this.isEmitted;
    }

    set setEmittedValue(value: string) {
        this.isEmitted = value;
    }

    getLastMetricListChange(): string[] {
        return this.lastMetricListChange;
    }

    getLastReportingSetChange(): string[] {
        return this.lastMetricReportingSetChange;
    }

    clearLastMetricChange(): void {
        this.lastMetricListChange = [];
        this.metricListChange = new Subject<any>();
        this.metricListChange$ = this.metricListChange.asObservable();
    }

    clearLastReportingSetChange(): void {
        this.lastMetricReportingSetChange = [];
        this.reportingSetListChange = new Subject<any>();
        this.reportingSetListChange$ = this.reportingSetListChange.asObservable();
    }
}
